import { enuFromGeodetic, type GeoPoint } from './GeoMath';

export interface GeoSample {
  lat: number;
  lon: number;
  accuracy: number;
  timestamp: number;
}

export interface SmoothedGeo extends GeoSample {
  /** 平滑化済みの原点基準ENU[m] */
  east: number;
  north: number;
}

/** accuracyがこれより悪いサンプルは棄却(計画書§7.1) */
const ACCURACY_REJECT_M = 25;
/** 指数移動平均の係数 */
const EMA_ALPHA = 0.3;

/**
 * navigator.geolocation.watchPosition のラッパー。
 * accuracy閾値による棄却とEMA平滑化を行い、原点基準のENU座標を提供する。
 * `?geo=lat,lon` クエリでデスクトップ検証用に現在地を固定できる。
 */
export class GeoWatcher {
  private watchId: number | null = null;
  private origin: GeoPoint;
  private smoothed: SmoothedGeo | null = null;
  private lastRaw: GeoSample | null = null;
  private override: GeoPoint | null = null;
  /** これまでに採用したサンプルの最良accuracy。粗い初期フィックスの置き換え判定に使う */
  private bestAccuracy = Infinity;
  private listeners = new Set<(s: SmoothedGeo) => void>();
  private errorListeners = new Set<(e: GeolocationPositionError) => void>();

  constructor(origin: GeoPoint, override?: GeoPoint | null) {
    this.origin = origin;
    this.override = override ?? null;
  }

  /** 原点を差し替える(現在地アンカー切替用)。ENUを再計算するため平滑化状態をリセットする */
  setOrigin(origin: GeoPoint): void {
    this.origin = { lat: origin.lat, lon: origin.lon };
    this.smoothed = null;
    if (this.override) {
      this.start(); // override時はwatchが無いので即座に再計算
    }
  }

  /**
   * GPSを使わず固定位置として振る舞わせる(GPS取得失敗時のフォールバック)。
   * 原点と同じ座標を与えれば「ユーザー=原点(ENU 0,0)」として全系が動作する。
   * start()前に呼ぶこと。
   */
  useFixedPosition(p: GeoPoint): void {
    this.stop();
    this.override = { lat: p.lat, lon: p.lon };
    this.smoothed = null;
  }

  start(): void {
    if (this.override) {
      const s: GeoSample = {
        lat: this.override.lat,
        lon: this.override.lon,
        accuracy: 1,
        timestamp: Date.now(),
      };
      this.accept(s);
      return;
    }
    if (this.watchId !== null) return;
    this.watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const s: GeoSample = {
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          timestamp: pos.timestamp,
        };
        this.lastRaw = s;
        // 良精度サンプルは常に採用。粗い初期フィックスしか無い間は、
        // それより精度が改善するサンプルも採用する(悪い初期値に固着させない)
        if (s.accuracy <= ACCURACY_REJECT_M || this.smoothed === null || s.accuracy < this.bestAccuracy) {
          this.accept(s);
        }
      },
      (err) => {
        for (const l of this.errorListeners) l(err);
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 },
    );
  }

  private accept(s: GeoSample): void {
    this.bestAccuracy = Math.min(this.bestAccuracy, s.accuracy);
    const enu = enuFromGeodetic(this.origin, s);
    if (this.smoothed === null) {
      this.smoothed = { ...s, east: enu.east, north: enu.north };
    } else {
      // 精度が悪いサンプルほど平滑化への寄与を下げる(固定座標アンカー時の
      // 表示ズレはGPS誤差が直接見えるため、ノイズの影響を最小化する)
      const a = EMA_ALPHA * Math.min(1, ACCURACY_REJECT_M / Math.max(s.accuracy, 1));
      this.smoothed = {
        ...s,
        lat: this.smoothed.lat + a * (s.lat - this.smoothed.lat),
        lon: this.smoothed.lon + a * (s.lon - this.smoothed.lon),
        east: this.smoothed.east + a * (enu.east - this.smoothed.east),
        north: this.smoothed.north + a * (enu.north - this.smoothed.north),
      };
    }
    for (const l of this.listeners) l(this.smoothed);
  }

  stop(): void {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
  }

  /** 平滑化済みの最新値(未取得ならnull) */
  get current(): SmoothedGeo | null {
    return this.smoothed;
  }

  /** 棄却前の生値(HUD表示用) */
  get raw(): GeoSample | null {
    return this.lastRaw;
  }

  onUpdate(fn: (s: SmoothedGeo) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  onError(fn: (e: GeolocationPositionError) => void): () => void {
    this.errorListeners.add(fn);
    return () => this.errorListeners.delete(fn);
  }
}
